import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  Fade,
  Slide,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  FiberManualRecord as DotIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const WhatsNewModal = ({ open, onClose, items: incomingItems = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [orderedItems, setOrderedItems] = useState(incomingItems);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Randomise the display order each time the modal opens (Fisher-Yates on a COPY — never
  // mutate the incoming prop). Shuffling once per open keeps navigation (arrows/dots) stable
  // while it's on screen; the order is fresh again the next time it opens. Callers pass the
  // stable module-level whatsNewItems array, so this effect only fires on open, not per render.
  useEffect(() => {
    if (open) {
      const shuffled = [...incomingItems];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setOrderedItems(shuffled);
      setCurrentIndex(0);
      setIsImageLoading(true);
    }
  }, [open, incomingItems]);

  // All rendering + navigation below reads the shuffled order.
  const items = orderedItems;

  const handleNext = () => {
    setSlideDirection('left');
    setCurrentIndex((prev) => (prev + 1) % items.length);
    setIsImageLoading(true);
  };

  const handlePrev = () => {
    setSlideDirection('right');
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    setIsImageLoading(true);
  };

  const handleDotClick = (index) => {
    if (index !== currentIndex) {
      setSlideDirection(index > currentIndex ? 'left' : 'right');
      setCurrentIndex(index);
      setIsImageLoading(true);
    }
  };

  const handleClose = () => {
    setCurrentIndex(0);
    onClose();
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!open) return;
      
      if (event.key === 'ArrowLeft') {
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [open, items.length]);

  // Category color mapping
  const getCategoryColor = (category) => {
    const colorMap = {
      'New Feature': 'primary',
      'Enhancement': 'success',
      'Bug Fix': 'warning',
      'Breaking Change': 'error',
      'Feature Update': 'info',
    };
    return colorMap[category] || 'default';
  };

  if (!items.length) return null;

  const currentItem = items[currentIndex];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Fade}
      transitionDuration={300}
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : 600,
          maxHeight: isMobile ? '100vh' : '85vh',
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 24px 48px rgba(0,0,0,0.4)'
            : '0 24px 48px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2,
        px: 3,
        pt: 3,
        background: 'transparent',
        position: 'relative',
        zIndex: 100,
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent 0%, ${theme.palette.divider} 50%, transparent 100%)`,
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="h4" 
            component="div" 
            sx={{ 
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            What's New
          </Typography>
          {items.length > 1 && (
            <Chip 
              label={`${currentIndex + 1} of ${items.length}`}
              size="small"
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                fontWeight: 600,
                backgroundColor: theme.palette.action.hover
              }}
            />
          )}
        </Box>
        <IconButton 
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleClose();
          }}
          size="large"
          aria-label="Close modal"
          data-testid="close-modal-button"
          sx={{
            color: theme.palette.text.secondary,
            transition: 'all 0.2s ease-in-out',
            zIndex: 1000,
            position: 'relative',
            cursor: 'pointer',
            '&:hover': {
              color: theme.palette.error.main,
              backgroundColor: theme.palette.error.main + '10',
              transform: 'scale(1.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ 
        p: 0, 
        overflow: 'hidden', 
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        {/* Progress bar for multiple items */}
        {items.length > 1 && (
          <LinearProgress 
            variant="determinate" 
            value={(currentIndex + 1) / items.length * 100}
            sx={{
              height: 3,
              backgroundColor: theme.palette.action.hover,
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                transition: 'transform 0.4s ease-in-out'
              }
            }}
          />
        )}

        <Box sx={{ 
          position: 'relative', 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0 // Important for flex shrinking
        }}>
          {/* Navigation Arrows */}
          {items.length > 1 && (
            <>
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  width: 56,
                  height: 56,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    transform: 'translateY(-50%) scale(1.1)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                  }
                }}
              >
                <ArrowBackIcon fontSize="large" />
              </IconButton>
              
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  width: 56,
                  height: 56,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    transform: 'translateY(-50%) scale(1.1)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
                  }
                }}
              >
                <ArrowForwardIcon fontSize="large" />
              </IconButton>
            </>
          )}

          {/* Content Card with Animation */}
          <Slide 
            direction={slideDirection} 
            in={true} 
            key={currentIndex}
            timeout={400}
          >
            <Card sx={{ 
              flex: 1,
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundColor: 'transparent',
              overflow: 'hidden',
              minHeight: 0 // Important for proper flex behavior
            }}>
              {/* Image Section */}
              {currentItem.image && (
                <Box sx={{ 
                  width: isMobile ? '100%' : '50%',
                  height: isMobile ? 250 : 'auto',
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  {isImageLoading && (
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.action.hover,
                      zIndex: 1
                    }}>
                      <LinearProgress sx={{ width: '80%' }} />
                    </Box>
                  )}
                  <CardMedia
                    component="img"
                    sx={{
                      width: '100%',
                      height: isMobile ? 250 : '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease-in-out',
                      display: 'block',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                    image={currentItem.image}
                    alt={currentItem.title}
                    onLoad={handleImageLoad}
                    onError={handleImageLoad}
                  />
                  {/* Image overlay gradient */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30%',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
                    pointerEvents: 'none'
                  }} />
                </Box>
              )}

              {/* Content Section */}
              <CardContent sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                p: isMobile ? 3 : 4,
                gap: 2,
                position: 'relative',
                minHeight: 0 // Important for flex shrinking
              }}>
                {/* Category Badge */}
                {currentItem.category && (
                  <Fade in={true} timeout={600}>
                    <Box>
                      <Chip
                        label={currentItem.category}
                        color={getCategoryColor(currentItem.category)}
                        variant="filled"
                        size="medium"
                        sx={{ 
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      />
                    </Box>
                  </Fade>
                )}

                {/* Title */}
                <Fade in={true} timeout={800}>
                  <Typography 
                    variant="h3" 
                    component="h2" 
                    sx={{ 
                      fontWeight: 800,
                      lineHeight: 1.2,
                      color: theme.palette.text.primary,
                      fontSize: { xs: '1.75rem', md: '2.5rem' }
                    }}
                  >
                    {currentItem.title}
                  </Typography>
                </Fade>

                {/* Description */}
                <Fade in={true} timeout={1000}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ 
                      lineHeight: 1.7,
                      fontSize: '1.1rem',
                      maxWidth: '90%'
                    }}
                  >
                    {currentItem.description}
                  </Typography>
                </Fade>

                {/* Features List */}
                {currentItem.features && currentItem.features.length > 0 && (
                  <Fade in={true} timeout={1200}>
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <CheckIcon color="success" fontSize="small" />
                        Key Features
                      </Typography>
                      <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: 1
                      }}>
                        {currentItem.features.map((feature, index) => (
                          <Box 
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor: theme.palette.action.hover,
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: theme.palette.action.selected,
                                transform: 'translateX(4px)'
                              }
                            }}
                          >
                            <CheckIcon 
                              sx={{ 
                                fontSize: 16, 
                                color: theme.palette.success.main 
                              }} 
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Fade>
                )}

                <Box sx={{ mt: 'auto' }}>
                  <Divider sx={{ my: 2, opacity: 0.5 }} />
                  
                  {/* Action Button and Date */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    {currentItem.actionUrl && (
                      <Fade in={true} timeout={1400}>
                        <Button
                          variant="contained"
                          size="large"
                          href={currentItem.actionUrl}
                          target={currentItem.external ? '_blank' : '_self'}
                          endIcon={currentItem.external ? <OpenInNewIcon /> : null}
                          sx={{ 
                            minWidth: 150,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            py: 1.5,
                            px: 3,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                            }
                          }}
                        >
                          {currentItem.actionText || 'Learn More'}
                        </Button>
                      </Fade>
                    )}

                    {/* Date */}
                    {currentItem.date && (
                      <Fade in={true} timeout={1600}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          color: theme.palette.text.disabled
                        }}>
                          <ScheduleIcon fontSize="small" />
                          <Typography variant="caption" sx={{ fontSize: '0.875rem' }}>
                            {new Date(currentItem.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      </Fade>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Slide>

          {/* Enhanced Dots Indicator */}
          {items.length > 1 && (
            <Fade in={true} timeout={800}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 1,
                px: 2,
                bgcolor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                backdropFilter: 'blur(10px)',
                zIndex: 5,
                flexShrink: 0 // Prevent shrinking
              }}>
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 3,
                  p: 1,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {items.map((_, index) => (
                    <IconButton
                      key={index}
                      size="small"
                      onClick={() => handleDotClick(index)}
                      sx={{
                        width: 12,
                        height: 12,
                        minWidth: 12,
                        p: 0,
                        borderRadius: '50%',
                        bgcolor: index === currentIndex 
                          ? theme.palette.primary.main 
                          : theme.palette.action.disabled,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          bgcolor: index === currentIndex 
                            ? theme.palette.primary.dark 
                            : theme.palette.action.hover,
                          transform: 'scale(1.3)'
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Fade>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
